// src/components/roads/page/shared.js
// Shared building blocks for RoadWorksPage — extracted unchanged from RoadWorksPage v4.7
// (motion presets, API helper, debug logger, status configs, cards and headers).
// --- UPDATE: v5.0 additions — formatRelative(), isAuthError(), rulBand(), useNow()
import React, { memo, useEffect, useState } from 'react';
import {
    Box, Badge, Flex, HStack, Icon, Text, VStack, Skeleton, Spinner, Grid,
    useColorModeValue,
} from '@chakra-ui/react';
import {
    MdCheckCircle, MdWarning, MdConstruction, MdError, MdSchedule,
    MdFiberManualRecord, MdTrendingUp, MdTrendingDown,
} from 'react-icons/md';
import { motion } from 'framer-motion';
import { config } from '../../../config';
import { getAuthToken } from '../../../utils/auth';

// ─────────────────────────────────────────────────────────────────────────────
// MOTION WRAPPERS
// ─────────────────────────────────────────────────────────────────────────────
export const MotionBox  = motion(Box);
export const MotionFlex = motion(Flex);
export const MotionGrid = motion(Grid);
export const MotionText = motion(Text);

export const ANIM = {
    fadeInUp:     { initial:{ opacity:0, y:20 },  animate:{ opacity:1, y:0 } },
    fadeInDown:   { initial:{ opacity:0, y:-20 }, animate:{ opacity:1, y:0 } },
    fadeInLeft:   { initial:{ opacity:0, x:-20 }, animate:{ opacity:1, x:0 } },
    fadeInRight:  { initial:{ opacity:0, x:20 },  animate:{ opacity:1, x:0 } },
    scaleIn:      { initial:{ opacity:0, scale:0.85 }, animate:{ opacity:1, scale:1 } },
    slideIn:      { initial:{ opacity:0, x:-12 }, animate:{ opacity:1, x:0 } },
    stagger:      (i, base=0.04) => ({ delay: i * base }),
    springBounce: { type:'spring', stiffness:300, damping:24 },
    smoothEase:   { duration:0.35, ease:[0.25, 0.46, 0.45, 0.94] },
};

// ─────────────────────────────────────────────────────────────────────────────
// API SERVICE LAYER
// ─────────────────────────────────────────────────────────────────────────────
export const API_BASE = process.env.REACT_APP_API_URL || config?.apiUrl || 'http://localhost:3001/api/v1';

export const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getAuthToken() || ''}`,
});

export const apiFetch = (path, opts = {}) =>
    fetch(`${API_BASE}${path}`, { headers: authHeaders(), ...opts })
        .then(async r => {
            const json = await r.json().catch(() => ({}));
            if (!r.ok) {
                const err = new Error(json.message || `HTTP ${r.status}`);
                // --- UPDATE: keep the status so auth expiry can be detected reliably
                err.status = r.status;
                if (r.status === 401) err.name = 'AuthError';
                // --- END UPDATE
                throw err;
            }
            return json;
        });

// --- UPDATE: single auth-expiry test used by the page, hook and event bus
export const isAuthError = (err) =>
    err?.name === 'AuthError' ||
    err?.status === 401 ||
    err?.response?.status === 401 ||
    String(err?.message || '').includes('401');
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// DEBUG LOGGER
// ─────────────────────────────────────────────────────────────────────────────
export class DebugLogger {
    constructor(name) {
        this.name      = name;
        this.logs      = [];
        this.listeners = [];
        this.enabled   = process.env.NODE_ENV === 'development';
        this._perfMarks = {};
    }
    log(level, message, data = null) {
        if (!this.enabled) return;
        const entry = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
            timestamp: new Date().toISOString(),
            level, message, data, component: this.name,
        };
        this.logs.push(entry);
        if (this.logs.length > 500) this.logs.shift();
        this.listeners.forEach(l => l(entry));
        const s = {
            debug:'color:#888;font-size:11px', info:'color:#3182ce;font-weight:bold',
            warn:'color:#d69e2e;font-weight:bold', error:'color:#e53e3e;font-weight:bold',
            success:'color:#38a169;font-weight:bold',
        };
        if (data) console.log(`%c[${this.name}][${level.toUpperCase()}] ${message}`, s[level]||s.info, data);
        else      console.log(`%c[${this.name}][${level.toUpperCase()}] ${message}`, s[level]||s.info);
    }
    debug  (m,d){ this.log('debug',  m,d); }
    info   (m,d){ this.log('info',   m,d); }
    warn   (m,d){ this.log('warn',   m,d); }
    error  (m,d){ this.log('error',  m,d); }
    success(m,d){ this.log('success',m,d); }
    perfStart(label){ this._perfMarks[label] = performance.now(); }
    perfEnd(label){
        const start = this._perfMarks[label];
        if (start) {
            const dur = (performance.now() - start).toFixed(1);
            this.info(`⏱ ${label}: ${dur}ms`);
            delete this._perfMarks[label];
            return parseFloat(dur);
        }
        return 0;
    }
    subscribe(cb){ this.listeners.push(cb); return () => { this.listeners = this.listeners.filter(l=>l!==cb); }; }
    getLogs(lv=null){ return lv ? this.logs.filter(l=>l.level===lv) : [...this.logs]; }
    clear(){ this.logs=[]; }
    export(){ return JSON.stringify(this.logs, null, 2); }
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
export const CONDITION_CFG = {
    excellent:{ color:'green',  label:'Excellent', score:95, weight:5 },
    good:     { color:'teal',   label:'Good',      score:80, weight:4 },
    fair:     { color:'yellow', label:'Fair',      score:65, weight:3 },
    poor:     { color:'orange', label:'Poor',      score:50, weight:2 },
    critical: { color:'red',    label:'Critical',  score:30, weight:1 },
};

export const STATUS_CFG = {
    operational:          { color:'green',  label:'Operational',  icon:MdCheckCircle },
    maintenance_required: { color:'orange', label:'Maint. Needed', icon:MdWarning      },
    under_maintenance:    { color:'blue',   label:'In Progress',   icon:MdConstruction },
    closed:               { color:'red',    label:'Closed',        icon:MdError        },
    planned:              { color:'purple', label:'Planned',       icon:MdSchedule   },
    decommissioned:       { color:'gray',   label:'Decommissioned',icon:MdFiberManualRecord },
};

export const SEVERITY_CFG = {
    critical:{ color:'red',    label:'Critical' },
    high:    { color:'orange', label:'High'     },
    medium:  { color:'yellow', label:'Medium'   },
    low:     { color:'green',  label:'Low'      },
};

// --- UPDATE: RUL risk bands — same thresholds as the existing map heatmap (2 / 5 / 8 years)
export const RUL_BANDS = [
    { key:'critical', label:'Critical', max:2,        color:'red',    hex:'#E53E3E' },
    { key:'poor',     label:'Poor',     max:5,        color:'orange', hex:'#DD6B20' },
    { key:'fair',     label:'Fair',     max:8,        color:'yellow', hex:'#D69E2E' },
    { key:'healthy',  label:'Healthy',  max:Infinity, color:'green',  hex:'#38A169' },
];

export const rulBand = (years) => {
    if (years === null || years === undefined || !Number.isFinite(Number(years))) return null;
    return RUL_BANDS.find(b => Number(years) <= b.max) || RUL_BANDS[RUL_BANDS.length - 1];
};

export const formatRelative = (ts, now = Date.now()) => {
    if (!ts) return '—';
    const t = ts instanceof Date ? ts.getTime() : new Date(ts).getTime();
    if (!Number.isFinite(t)) return '—';
    const s = Math.max(0, Math.round((now - t) / 1000));
    if (s < 45) return 'just now';
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
};

/** Re-renders the caller every `intervalMs` so relative times stay fresh. */
export const useNow = (intervalMs = 30000) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
    return now;
};
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
export const PulseIndicator = ({ color='green', size=3 }) => (
    <Box position="relative" display="inline-flex"
        w={`${size*4}px`} h={`${size*4}px`} flexShrink={0}>
        <MotionBox
            position="absolute" inset={0} borderRadius="full" bg={`${color}.400`}
            animate={{ scale:[1,1.9,1], opacity:[0.8,0,0.8] }}
            transition={{ duration:2.2, repeat:Infinity, ease:'easeInOut' }}
        />
        <Box position="absolute" inset={0} borderRadius="full" bg={`${color}.500`} />
    </Box>
);

export const AIStatusBadge = ({ status }) => {
    const map = {
        idle:      { color:'gray',  label:'AI Idle',  spin:false, glow:false },
        thinking:  { color:'blue',  label:'Analyzing…', spin:true,  glow:true  },
        success:   { color:'green', label:'Synced',     spin:false, glow:true  },
        error:     { color:'red',   label:'Error',      spin:false, glow:false },
    };
    const c = map[status] || map.idle;
    return (
        <MotionBox
            animate={c.glow ? { boxShadow:[
                `0 0 0px var(--chakra-colors-${c.color}-400)`,
                `0 0 12px var(--chakra-colors-${c.color}-400)`,
                `0 0 0px var(--chakra-colors-${c.color}-400)`,
            ] } : {}}
            transition={{ duration:2, repeat:Infinity }}
            borderRadius="full"
        >
            <Badge colorScheme={c.color} px={3} py={1} borderRadius="full"
                display="inline-flex" alignItems="center" gap={2}
                boxShadow={c.glow ? `0 0 8px var(--chakra-colors-${c.color}-300)` : 'none'}>
                {c.spin && <Spinner size="xs" mr={1} />}
                <Text fontSize="xs" fontWeight="700">{c.label}</Text>
            </Badge>
        </MotionBox>
    );
};

export const ShimmerSkeleton = ({ lines=3, ...props }) => {
    const startColor = useColorModeValue('gray.100','gray.700');
    const endColor   = useColorModeValue('gray.300','gray.600');
    return (
        <VStack spacing={2} w="full" {...props}>
            {Array.from({ length: lines }, (_, i) => (
                <Skeleton
                    key={i} h="16px" w={i === lines - 1 ? '60%' : '100%'}
                    borderRadius="md" startColor={startColor} endColor={endColor}
                />
            ))}
        </VStack>
    );
};

export const EmptyState = ({ icon:IconComp, title, subtitle, action }) => (
    <Flex direction="column" align="center" justify="center" py={10} gap={3}>
        <MotionBox
            animate={{ y:[0,-6,0] }}
            transition={{ duration:3, repeat:Infinity, ease:'easeInOut' }}
        >
            <Icon as={IconComp} boxSize={14} color="gray.300" />
        </MotionBox>
        <Text fontWeight="700" color="gray.400" fontSize="sm">{title}</Text>
        {subtitle && <Text fontSize="xs" color="gray.400" textAlign="center" maxW="240px">{subtitle}</Text>}
        {/* --- UPDATE: optional call-to-action */}
        {action}
        {/* --- END UPDATE */}
    </Flex>
);

export const GlassCard = memo(({ children, accentColor, hoverLift=true, ...rest }) => {
    const glassBg     = useColorModeValue('rgba(255,255,255,0.78)','rgba(26,32,44,0.78)');
    const borderColor = useColorModeValue('gray.200','gray.700');
    return (
        <MotionBox
            whileHover={hoverLift ? { y:-3, boxShadow:'0 20px 50px rgba(0,0,0,0.12)' } : {}}
            transition={ANIM.smoothEase}
        >
            <Box
                bg={glassBg}
                backdropFilter="blur(16px) saturate(180%)"
                borderRadius="2xl"
                borderWidth="1px"
                borderColor={borderColor}
                overflow="hidden"
                position="relative"
                transition="all 0.3s"
                {...rest}
            >
                {accentColor && (
                    <Box position="absolute" top={0} left={0} right={0} h="3px"
                        bgGradient={`linear(to-r, ${accentColor}.400, ${accentColor}.600)`}
                        borderTopRadius="2xl" />
                )}
                {children}
            </Box>
        </MotionBox>
    );
});
GlassCard.displayName = 'GlassCard';

// --- UPDATE: hoverLift option (large tables shouldn't jump on hover)
export const SectionCard = memo(({ children, accentColor, hoverLift = true, ...rest }) => {
// --- END UPDATE
    const cardBg      = useColorModeValue('white','gray.800');
    const borderColor = useColorModeValue('gray.200','gray.700');
    return (
        <MotionBox
            whileHover={hoverLift ? { y:-2, boxShadow:'0 16px 40px rgba(0,0,0,0.1)' } : {}}
            transition={ANIM.smoothEase}
        >
            <Box bg={cardBg} borderRadius="2xl" borderWidth="1px"
                borderColor={borderColor} overflow="hidden"
                position="relative" transition="all 0.3s" {...rest}>
                {accentColor && (
                    <Box position="absolute" top={0} left={0} right={0} h="3px"
                        bgGradient={`linear(to-r, ${accentColor}.400, ${accentColor}.600)`}
                        borderTopRadius="2xl" />
                )}
                {children}
            </Box>
        </MotionBox>
    );
});
SectionCard.displayName = 'SectionCard';

export const PanelHeader = memo(({ iconBg, icon:IconComp, title, subtitle, right, badge }) => {
    const borderColor = useColorModeValue('gray.200','gray.700');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    return (
        <Flex px={5} py={4} borderBottomWidth="1px" borderColor={borderColor}
            align="center" justify="space-between" gap={3} flexWrap="wrap">
            <HStack spacing={3}>
                <MotionBox
                    whileHover={{ rotate:8, scale:1.1 }}
                    transition={{ type:'spring', stiffness:400 }}
                >
                    <Box bg={iconBg} p={2.5} borderRadius="xl"
                        boxShadow={`0 4px 14px var(--chakra-colors-${iconBg?.split('.')[0] || 'blue'}-300)`}>
                        <Icon as={IconComp} color="white" boxSize={5} />
                    </Box>
                </MotionBox>
                <Box>
                    {/* --- UPDATE: optional badge (e.g. an AI/ML chip) next to the title */}
                    <HStack spacing={2}>
                        <Text fontWeight="800" fontSize="sm">{title}</Text>
                        {badge}
                    </HStack>
                    {/* --- END UPDATE */}
                    {subtitle && <Text fontSize="xs" color={mutedColor}>{subtitle}</Text>}
                </Box>
            </HStack>
            {right}
        </Flex>
    );
});
PanelHeader.displayName = 'PanelHeader';

export const StatCard = memo(({ label, value, icon:IconComp, color='blue', helpText,
    isLoading, onClick, isActive, liveIndicator, delta, badge }) => {
    const cardBg       = useColorModeValue('white','gray.800');
    const borderColor  = useColorModeValue('gray.200','gray.700');
    const labelColor   = useColorModeValue('gray.500','gray.400');
    const valueColor   = useColorModeValue('gray.800','white');
    const iconBg       = useColorModeValue(`${color}.50`, `${color}.900`);
    const iconColor    = useColorModeValue(`${color}.600`,`${color}.300`);
    const activeBorder = useColorModeValue(`${color}.400`,`${color}.500`);

    return (
        <MotionBox
            whileHover={{ y:-4, boxShadow:'0 16px 40px rgba(0,0,0,0.14)' }}
            whileTap={onClick ? { scale:0.97 } : {}}
            transition={ANIM.smoothEase}
            cursor={onClick ? 'pointer' : 'default'}
            onClick={onClick}
        >
            <Box
                bg={cardBg} p={5} borderRadius="2xl" borderWidth="2px"
                borderColor={isActive ? activeBorder : borderColor}
                boxShadow={isActive ? `0 0 0 4px var(--chakra-colors-${color}-100)` : 'md'}
                position="relative" overflow="hidden" transition="all 0.3s"
                _hover={{ borderColor:`${color}.300` }}
            >
                {isActive && (
                    <MotionBox
                        layoutId="roadStatAccent"
                        position="absolute" top={0} left={0} right={0} h="3px"
                        bgGradient={`linear(to-r, ${color}.400, ${color}.700)`}
                        borderTopRadius="2xl"
                    />
                )}
                <Box position="absolute" top="-12px" right="-12px" w="80px" h="80px"
                    bg={iconBg} opacity={0.5} borderRadius="full" filter="blur(14px)"
                />
                <Flex justify="space-between" align="flex-start">
                    <Box flex={1}>
                        <HStack spacing={2} mb={1}>
                            <Text fontSize="xs" fontWeight="700" color={labelColor}
                                textTransform="uppercase" letterSpacing="wider">
                                {label}
                            </Text>
                            {liveIndicator && <PulseIndicator color={color} size={2} />}
                            {/* --- UPDATE */}
                            {badge}
                            {/* --- END UPDATE */}
                        </HStack>
                        {isLoading ? (
                            <Skeleton h="38px" w="70px" borderRadius="lg" mt={1} />
                        ) : (
                            <HStack spacing={2} align="baseline" mt={0.5}>
                                <MotionBox
                                    key={value}
                                    initial={{ opacity:0, y:10 }}
                                    animate={{ opacity:1, y:0 }}
                                    transition={{ type:'spring', stiffness:200, damping:20 }}
                                >
                                    <Text fontSize="3xl" fontWeight="900" color={valueColor} lineHeight="1">
                                        {value ?? '—'}
                                    </Text>
                                </MotionBox>
                                {delta !== undefined && delta !== 0 && (
                                    <MotionBox initial={{ scale:0 }} animate={{ scale:1 }} transition={ANIM.springBounce}>
                                        <Badge colorScheme={delta > 0 ? 'green' : 'red'} fontSize="2xs" borderRadius="full"
                                            display="flex" alignItems="center" gap={0.5}>
                                            <Icon as={delta > 0 ? MdTrendingUp : MdTrendingDown} boxSize={3} />
                                            {delta > 0 ? '+' : ''}{delta}
                                        </Badge>
                                    </MotionBox>
                                )}
                            </HStack>
                        )}
                        {helpText && (
                            <Text fontSize="2xs" color={labelColor} mt={1}>{helpText}</Text>
                        )}
                    </Box>
                    <MotionBox
                        whileHover={{ rotate:12, scale:1.1 }}
                        transition={{ type:'spring', stiffness:400 }}
                    >
                        <Box bg={iconBg} p={3} borderRadius="xl"
                            boxShadow={`0 4px 14px var(--chakra-colors-${color}-200)`}>
                            <Icon as={IconComp} boxSize={6} color={iconColor} />
                        </Box>
                    </MotionBox>
                </Flex>
            </Box>
        </MotionBox>
    );
});
StatCard.displayName = 'StatCard';
